import BigNumber from 'bignumber.js';

BigNumber.set({ EXPONENTIAL_AT: 100 });

/**
 * Converts a raw token amount (in smallest unit) to a human-readable decimal string.
 * Uses BigNumber.shiftedBy to avoid floating point precision issues.
 */
export function formatRawTokenAmount(
  rawAmount: bigint,
  decimals: number
): string {
  return BigNumber(rawAmount.toString()).shiftedBy(-decimals).toFixed();
}

/**
 * Parses a human-readable decimal string into a raw token amount (bigint).
 * Uses BigNumber.shiftedBy to avoid floating point precision issues.
 */
export function parseDecimalToRawAmount(
  decimalAmount: string,
  decimals: number
): bigint {
  return BigInt(
    BigNumber(decimalAmount)
      .shiftedBy(decimals)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toFixed(0)
  );
}

/** Threshold for considering an approval "unlimited" - 10^50 is far above any realistic token supply */
const UNLIMITED_THRESHOLD = BigNumber('1e50');

/**
 * Returns true if the raw amount represents an "unlimited" approval
 * (either the string says "Unlimited" or the value exceeds 10^50).
 */
export function isUnlimitedApproval(
  displayAmount: string,
  rawAmount: bigint
): boolean {
  return (
    displayAmount === 'Unlimited' ||
    BigNumber(rawAmount.toString()).isGreaterThan(UNLIMITED_THRESHOLD)
  );
}

/**
 * Formats a decimal number string for display with locale formatting
 * and a maximum number of decimal places.
 */
export function formatDecimalsForDisplay(
  value: string,
  maxDecimals: number
): string {
  const bn = BigNumber(value);
  if (bn.isNaN()) return value;

  if (!value.includes('.')) {
    return bn.toFormat(0);
  }

  const [, fraction] = value.split('.');
  const actualDecimals = fraction?.length ?? 0;
  const decimalsToUse = Math.min(actualDecimals, maxDecimals);

  return bn.toFormat(decimalsToUse);
}
